import i18next, { BackendModule, InitOptions, ReadCallback, Services } from 'i18next';
import { fetchNamespacedTemplates, TemplateResponse } from 'src/api/template';
import { createSharedRequests } from 'src/core/sharedRequests';
import { recordTextBlockSource } from 'src/core/translatorMode';

/** The fallback languages i18next loads implicitly (config/i18next: BASE_LANGUAGE, FALLBACK_LANGUAGE). */
const FALLBACK_LOCALES = ['en', 'en-at'];

/** Locales a component loads on purpose (e.g. the videos panel's English names) — never skipped. */
const explicitLocales = new Set<string>();

export function requestExplicitLocale(locale: string): void {
    explicitLocales.add(locale.toLowerCase());
}

/** The language the app runs in; i18next sets `language` before loading, `isLanguageChangingTo` (untyped) during a switch. */
function activeLanguage(): string {
    const instance = (i18next as unknown) as { language?: string; isLanguageChangingTo?: string };
    return (instance.isLanguageChangingTo || instance.language || '').toLowerCase();
}

/** How long a namespace answer is shared between the reads of one page load. */
const SHARED_FETCH_TTL_MS = 60_000;

/**
 * R38-3: text block key -> "<namespace>:<json path>" targets (generated by
 * scripts/textblocks/generate_textblock_maps.py). The Mein Bereich blocks live in TPL_NS_WEBSEITE,
 * the AdminPanel blocks in TPL_NS_ADMINPANEL; a block shared by both carries both targets.
 */
export type TextBlockMap = Record<string, string[]>;

const MAPPED_NAMESPACES: Record<string, string[]> = {
    translations: ['TPL_NS_WEBSEITE'],
    routes: ['TPL_NS_WEBSEITE'],
    webseite: ['TPL_NS_WEBSEITE'],
    adminpanel: ['TPL_NS_ADMINPANEL', 'TPL_NS_WEBSEITE'],
};

const loadMaps = async (namespace: string): Promise<TextBlockMap[]> => {
    if (namespace === 'adminpanel') {
        const [meinBereich, adminpanel] = await Promise.all([
            import('src/resources/textblocks/mein_bereich_map.json'),
            import('src/adminPanelV2/resources/textblocks/adminpanel_map.json'),
        ]);
        return [meinBereich.default as TextBlockMap, adminpanel.default as TextBlockMap];
    }
    const meinBereich = await import('src/resources/textblocks/mein_bereich_map.json');
    return [meinBereich.default as TextBlockMap];
};

class BackendError extends Error {
    retry: boolean;

    constructor(message, retry = false) {
        super(message);

        this.retry = retry;
    }
}

interface BackendOptions {}

interface Resource {
    [k: string]: Resource | string;
}

function normalize<T>(x: T | T[]): T[] {
    return Array.isArray(x) ? x : [x];
}

class I18NextBackend implements BackendModule<BackendOptions> {
    static type = 'backend';
    type: 'backend' = 'backend';

    services!: Services;
    options: BackendOptions = {};

    constructor(services: Services, options: BackendOptions, i18nextOptions: InitOptions) {
        this.init(services, options, i18nextOptions);
    }

    /**
     * LCP fix 24.09: one fetch per locale and namespace template, shared by the base read and
     * the text block overlay of every namespace (the adminpanel read and its overlay both need
     * TPL_NS_ADMINPANEL, four namespaces need TPL_NS_WEBSEITE).
     */
    private sharedFetches = createSharedRequests<TemplateResponse[]>(SHARED_FETCH_TTL_MS);

    init(services: Services, options: BackendOptions, i18nextOptions: InitOptions) {
        this.services = services;
        this.options = {
            ...this.options,
            ...options,
        };
    }

    read(locale: string, namespace: string, callback: ReadCallback) {
        // LCP fix 24.09: the implicit fallback reads (en, en-at) are skipped while the active
        // language is not English — the bundled German texts are the fallback. Locales a
        // component asks for explicitly (requestExplicitLocale) still read.
        const requested = locale.toLowerCase();
        if (
            FALLBACK_LOCALES.includes(requested) &&
            !explicitLocales.has(requested) &&
            !activeLanguage().startsWith('en')
        ) {
            callback(null, null as never);
            return;
        }

        const [language, region] = this.splitIntoLangRegion(locale);

        // base namespace, overlay namespaces and key maps start together
        const overlay = this.startOverlay(language, region, namespace);
        this.fetchTranslationTemplates(language, region, namespace)
            .then(res => overlay.then(apply => apply(res)))
            .then(res => {
                callback(null, res);
            })
            .catch(e => {
                if (e instanceof BackendError) {
                    callback(e, e.retry);
                }
            });

        // this.loadTranslationFiles(language, namespace)
        //     .then(filesResource => {
        //         return this.fetchTranslationTemplates(language, region, namespace).then(templatesResource => {
        //             callback(null, merge(filesResource, templatesResource));
        //         });
        //     })
        //     .catch(e => {
        //         if (e instanceof BackendError) {
        //             callback(e, e.retry);
        //         }
        //     });
    }

    /**
     * Load our translation files
     */
    loadTranslationFiles(language: string, namespace: string): Promise<Resource> {
        const url = `${process.env.PUBLIC_URL}/locales/${language}/${namespace}.json`;

        return fetch(url)
            .then(
                response => {
                    const { ok, status } = response;

                    if (!ok) {
                        // don't retry for 4xx codes
                        const retry = status >= 500 && status < 600;

                        throw new BackendError(`failed loading translation file ${url}`, retry);
                    }

                    return response.text();
                },
                () => {
                    throw new BackendError(`failed loading translation file ${url}`);
                }
            )
            .then(data => {
                try {
                    return Promise.resolve(JSON.parse(data));
                } catch (e) {
                    throw new BackendError(`failed parsing translation file ${url}`);
                }
            });
    }

    /**
     * R38-3: the texts of Mein Bereich and the AdminPanel are text blocks under their own TPL keys;
     * the map puts every block value onto the json path the code reads (the bundled German file
     * stays the fallback for keys without a block or without this language).
     */
    async overlayMappedTextBlocks(
        resource: Resource,
        language: string,
        region: string,
        namespace: string
    ): Promise<Resource> {
        const apply = await this.startOverlay(language, region, namespace);
        return apply(resource);
    }

    /**
     * Fetch the translation templates from lamante backend
     */
    fetchTranslationTemplates(language: string, region: string, namespace: string): Promise<Resource> {
        return this.sharedFetch(language, region, intoExtNamespaceKey(namespace)).then(templates => {
            try {
                return Promise.resolve(this.parseTemplates(templates, namespace));
            } catch (e) {
                throw new BackendError(`failed parsing ${namespace} templates to json`, false);
            }
        });
    }

    fetchTemplates(language: string, region: string, namespaceKey: string): Promise<TemplateResponse[]> {
        return fetchNamespacedTemplates(language, region, namespaceKey).then(
            ({ response, error }) => {
                if (error) {
                    // Backend returns 404 if translation key does not exist
                    if (error.code === 404) return [];
                    // don't retry for 4xx codes
                    const retry = error.code >= 500 && error.code < 600;

                    throw new BackendError(error.message, retry);
                }
                return normalize(response!.data).filter(x => x !== undefined);
            },
            () => {
                throw new BackendError(`failed loading templates for namespace ${namespaceKey}`);
            }
        );
    }

    /**
     * "Parse" / Transform Response from backend to resource for i18next
     */
    parseTemplates(data: TemplateResponse[], namespace: string): Resource {
        const resource = {};

        data.forEach(template => {
            // Skip everything which is not plain text
            if (template.type !== 'PLAIN_TEXT') return;
            // Skip everything which has no templateKey
            // Requesting a NAMESPACE key will always yield results with templateKey
            if (template.templateKey === undefined) return;

            const path = this.normalizeTplKey(template.templateKey, namespace);
            recordTextBlockSource(namespace, path, template.templateKey);
            const keys = path.split('.');

            // Textblock values carry [[x]] instead of {{x}}: the templating service renders
            // every value through Mustache, which would swallow native i18next placeholders.
            this.walk(resource, keys, template.value.replace(/\[\[(\w+)\]\]/g, '{{$1}}'));
        });

        return resource;
    }

    /**
     * Recursivly crete the resource object
     */
    walk(resource: Resource, keys: string[], value: string): Resource {
        const key = keys.shift()!;

        if (keys.length > 0) {
            if (resource[key] === undefined) resource[key] = {};

            this.walk(resource[key] as Resource, keys, value);
        } else {
            resource[key] = value;
        }

        return resource;
    }

    /**
     * The Backend imposes some rules for the template keys
     * - new / most template keys start with TPL_
     * - namespace template keys are prefixed with TPL_NS_
     * - while not a strict requirement the keys are uppercase
     *
     * - the countries namespace key will deliver template keys which are prefixed with COUNTRY_
     * - the languages namespace key will deliver template keys which are prefixed with LANGUAGE_
     *
     */
    normalizeTplKey(key: string, ns: string): string {
        let norm = key.replace('TPL_', '');

        switch (ns) {
            case 'countries':
                norm = norm.replace('COUNTRY_', '');
                break;
            case 'languages':
                norm = norm.replace('LANGUAGE_', '');
                break;
        }

        return norm.toLocaleLowerCase();
    }

    splitIntoLangRegion(locale: string): string[] {
        return locale.split('-');
    }

    /**
     * Save the missing translation
     * This needs to talk to the lamante backend at some point
     */
    create(languages: string[], namespace: string, key: string, fallbackValue: string) {
        console.log('i18next - lamante backend - create -> !not implemented!');
    }

    /** Starts the overlay fetches and the map import at once; resolves to the function that applies them. */
    private startOverlay(
        language: string,
        region: string,
        namespace: string
    ): Promise<(resource: Resource) => Resource> {
        const namespaceKeys = MAPPED_NAMESPACES[namespace];
        if (!namespaceKeys) return Promise.resolve(resource => resource);
        return Promise.all([
            loadMaps(namespace),
            Promise.all(
                namespaceKeys.map(namespaceKey =>
                    this.sharedFetch(language, region, namespaceKey).catch(() => [] as TemplateResponse[])
                )
            ),
        ]).then(([maps, templateLists]) => (resource: Resource) => {
            templateLists.forEach(templates => overlayTextBlocks(resource, templates, namespace, maps));
            return resource;
        });
    }

    private sharedFetch(language: string, region: string, namespaceKey: string): Promise<TemplateResponse[]> {
        return this.sharedFetches.get(`${language}-${region}|${namespaceKey}`, () =>
            this.fetchTemplates(language, region, namespaceKey)
        );
    }
    //   /** Load multiple languages and namespaces. For backends supporting multiple resources loading */
    //   readMulti?(languages: string[], namespaces: string[], callback: ReadCallback): void;
    //   /** Store the translation. For backends acting as cache layer */
    //   save?(language: string, namespace: string, data: ResourceLanguage): void;
}

export default I18NextBackend;

export function intoExtNamespaceKey(ns: string): string {
    return `TPL_NS_${ns.toUpperCase()}`;
}

export function intoExtKey(ns: string, key: string): string {
    let prefix: string;

    switch (ns) {
        case 'countries':
            prefix = 'COUNTRY';
            break;
        case 'languages':
            prefix = 'LANGUAGE';
            break;
        default:
            prefix = 'TPL';
    }
    return `${prefix}_${key.toUpperCase()}`;
}

/** Textblock values carry [[x]] instead of {{x}} (see parseTemplates). */
export const textBlockValue = (value: string): string => value.replace(/\[\[(\w+)\]\]/g, '{{$1}}');

export function overlayTextBlocks(
    resource: Resource,
    templates: TemplateResponse[],
    namespace: string,
    maps: TextBlockMap[]
): Resource {
    templates.forEach(template => {
        if (template.type !== 'PLAIN_TEXT' || template.templateKey === undefined) return;
        maps.forEach(map => {
            (map[template.templateKey!] ?? []).forEach(target => {
                const separator = target.indexOf(':');
                if (target.slice(0, separator) !== namespace) return;
                recordTextBlockSource(namespace, target.slice(separator + 1), template.templateKey!);
                setPath(resource, target.slice(separator + 1).split('.'), textBlockValue(template.value));
            });
        });
    });
    return resource;
}

function setPath(resource: Resource, keys: string[], value: string): void {
    const [head, ...rest] = keys;
    if (rest.length === 0) {
        resource[head] = value;
        return;
    }
    if (typeof resource[head] !== 'object' || resource[head] === null) resource[head] = {};
    setPath(resource[head] as Resource, rest, value);
}
